import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Eye, EyeOff, HelpCircle, Loader2, Plus, Radio, Trash2, Youtube } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PLATFORMS = [
  { id: "youtube", label: "YouTube", url: "rtmps://a.rtmps.youtube.com/live2" },
  { id: "facebook", label: "Facebook", url: "rtmps://live-api-s.facebook.com:443/rtmp/" },
  { id: "twitch", label: "Twitch", url: "rtmp://ingest.global-contribute.live-video.net/app" },
  { id: "custom", label: "Custom RTMP", url: "" },
];

const YT_STEPS = [
  "Open YouTube Studio (studio.youtube.com) on a computer and click Create → Go live.",
  "Pick Stream (not Webcam). Give the stream a title and set it to Public or Unlisted.",
  "Under Stream settings, copy the Stream key (click Copy). Leave YouTube open.",
  "Paste the key below and Save. It stays encrypted here — you only do this once.",
  "On Friday: press Go live in the console. YouTube starts receiving video in ~20 seconds, then hit Go live in YouTube Studio too (or turn on auto-start there).",
];

const err = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);

function AddDestinationDialog({ open, onOpenChange, onSaved }) {
  const [platform, setPlatform] = useState("youtube");
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [url, setUrl] = useState("");
  const [watch, setWatch] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const preset = PLATFORMS.find((p) => p.id === platform);

  const save = async () => {
    if (!key.trim()) { toast.error("Paste the stream key first"); return; }
    if (platform === "custom" && !url.trim()) { toast.error("Custom destinations need an RTMP URL"); return; }
    setSaving(true);
    try {
      await api.post("/simulcast/destinations", { platform, label: label.trim() || null, stream_key: key.trim(), rtmp_url: platform === "custom" ? url.trim() : null, enabled: true, watch_url: watch.trim() || null });
      toast.success(`${preset.label} added — it will start automatically when you go live`);
      setKey(""); setLabel(""); setUrl(""); setWatch("");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(err(e, "Couldn't save the destination")); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="simulcast-add-dialog" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Also stream to…</DialogTitle>
          <DialogDescription>Cloudflare re-sends your live program to this platform. Nothing changes about how you run the show.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2" data-testid="simulcast-platforms">
            {PLATFORMS.map((p) => (
              <button key={p.id} type="button" onClick={() => setPlatform(p.id)} className={`rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${platform === p.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`} data-testid={`simulcast-platform-${p.id}`}>{p.label}</button>
            ))}
          </div>
          {platform === "youtube" && (
            <ol className="space-y-1.5 rounded-lg border border-border bg-secondary/50 p-3 text-xs leading-5 text-muted-foreground" data-testid="simulcast-youtube-guide">
              {YT_STEPS.map((s, i) => <li key={i} className="flex gap-2"><span className="font-semibold text-primary">{i + 1}.</span><span>{s}</span></li>)}
            </ol>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground" htmlFor="simulcast-key">Stream key</label>
            <div className="mt-1 flex gap-2">
              <Input id="simulcast-key" type={showKey ? "text" : "password"} value={key} onChange={(e) => setKey(e.target.value)} placeholder="Paste the key from YouTube Studio" autoComplete="off" data-testid="simulcast-key-input" />
              <Button type="button" variant="outline" size="icon" onClick={() => setShowKey((v) => !v)} aria-label="Toggle key visibility" data-testid="simulcast-key-toggle">{showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground" htmlFor="simulcast-label">Label (optional)</label>
            <Input id="simulcast-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`e.g. ${preset.label} — Friday Conversations channel`} className="mt-1" data-testid="simulcast-label-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground" htmlFor="simulcast-watch">Public watch link (optional)</label>
            <Input id="simulcast-watch" value={watch} onChange={(e) => setWatch(e.target.value)} placeholder={platform === "youtube" ? "https://www.youtube.com/@YourChannel/live" : "https://…"} className="mt-1" data-testid="simulcast-watch-input" />
            <p className="mt-1 text-[11px] text-muted-foreground">Shown as a “Watch on {preset.label}” button next to the live player on the website.{platform === "youtube" ? " Your channel’s /live address always points at the current stream." : ""}</p>
          </div>
          {platform === "custom" ? (
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="simulcast-url">RTMP / RTMPS URL</label>
              <Input id="simulcast-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="rtmps://…" className="mt-1" data-testid="simulcast-url-input" />
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">Server: <code className="rounded bg-secondary px-1">{preset.url}</code> (set automatically)</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="simulcast-add-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="simulcast-add-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save destination</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Also stream to" — manage YouTube/Facebook/Twitch/RTMP destinations and see which are
 * receiving the program right now. Dark console styling. `liveId` + `live` drive live status.
 */
export function SimulcastPanel({ liveId, live = false }) {
  const [dests, setDests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const load = useCallback(async () => {
    try {
      if (liveId && live) {
        const r = await api.get(`/simulcast/live/${liveId}/status`);
        setDests(r.data.destinations || []);
      } else {
        const r = await api.get("/simulcast/destinations");
        setDests(r.data || []);
      }
    } catch (e) { /* keep last state */ } finally { setLoading(false); }
  }, [liveId, live]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!(liveId && live)) return undefined;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [liveId, live, load]);

  const setEnabled = async (d, enabled) => {
    setBusy(d.dest_id);
    try { await api.put(`/simulcast/destinations/${d.dest_id}`, { enabled }); await load(); }
    catch (e) { toast.error(err(e, "Couldn't update")); } finally { setBusy(null); }
  };
  const toggleLive = async (d) => {
    setBusy(d.dest_id);
    try {
      if (d.applied) { await api.delete(`/simulcast/live/${liveId}/apply/${d.dest_id}`); toast.success(`Stopped streaming to ${d.label}`); }
      else { await api.post(`/simulcast/live/${liveId}/apply/${d.dest_id}`); toast.success(`Now also streaming to ${d.label}`); }
      await load();
    } catch (e) { toast.error(err(e, "Cloudflare couldn't switch that destination")); } finally { setBusy(null); }
  };
  const [editing, setEditing] = useState(null); // { dest_id, value }
  const saveWatch = async () => {
    if (!editing) return;
    setBusy(editing.dest_id);
    try { await api.put(`/simulcast/destinations/${editing.dest_id}`, { watch_url: editing.value.trim() }); toast.success("Watch link saved"); setEditing(null); await load(); }
    catch (e) { toast.error(err(e, "Couldn't save the link")); } finally { setBusy(null); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.label}? You'd need to paste the stream key again to add it back.`)) return;
    setBusy(d.dest_id);
    try { await api.delete(`/simulcast/destinations/${d.dest_id}`); toast.success("Destination removed"); await load(); }
    catch (e) { toast.error(err(e, "Couldn't remove")); } finally { setBusy(null); }
  };

  return (
    <section className="rounded-xl border border-white/10 bg-[#0f1520]" data-testid="simulcast-panel">
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
        <Radio className="h-4 w-4 text-[#e6a25f]" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Also stream to</span>
        <button type="button" onClick={() => setShowGuide((v) => !v)} className="text-zinc-500 hover:text-zinc-200" title="How does this work?" data-testid="simulcast-help"><HelpCircle className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => setAdding(true)} className="ml-auto inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-xs font-medium text-zinc-200 hover:bg-white/5" data-testid="simulcast-add-button"><Plus className="h-3.5 w-3.5" /> Add</button>
      </div>
      {showGuide && (
        <div className="border-b border-white/10 px-4 py-3 text-xs leading-5 text-zinc-400" data-testid="simulcast-guide">
          Add YouTube once (stream key from YouTube Studio → Go live → Stream). Every time you press <span className="text-zinc-200">Go live</span>, Cloudflare also pushes the program there — no extra software, no second encoder. Toggle a destination off to skip it for a show.
        </div>
      )}
      <div className="px-4 py-3">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-zinc-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
        ) : dests.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/15 p-4 text-center" data-testid="simulcast-empty">
            <Youtube className="mx-auto h-6 w-6 text-zinc-500" />
            <p className="mt-2 text-sm text-zinc-300">Not simulcasting yet</p>
            <p className="mt-1 text-xs text-zinc-500">Add your YouTube channel and every live show will also go out there automatically.</p>
            <Button size="sm" className="mt-3" onClick={() => setAdding(true)} data-testid="simulcast-add-first"><Plus className="mr-1 h-3.5 w-3.5" /> Add YouTube</Button>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="simulcast-list">
            {dests.map((d) => {
              const streaming = live && d.applied;
              return (
                <li key={d.dest_id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`simulcast-dest-${d.dest_id}`}>
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${streaming ? "bg-red-500 shadow-[0_0_0_4px_rgba(239,68,68,0.25)]" : d.enabled ? "bg-emerald-400/70" : "bg-zinc-600"}`} data-testid="simulcast-dest-dot" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-zinc-100">{d.label}</p>
                    <p className="truncate text-[11px] text-zinc-500">
                      {d.platform_label} · key ••••{d.key_hint}
                      {live ? (streaming ? " · streaming now" : " · not streaming") : d.enabled ? " · starts with Go live" : " · off"}
                    </p>
                    {editing?.dest_id === d.dest_id ? (
                      <div className="mt-1.5 flex gap-1.5">
                        <input value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} placeholder="https://www.youtube.com/@YourChannel/live" className="h-7 min-w-0 flex-1 rounded-md border border-white/15 bg-black/30 px-2 text-xs text-zinc-100 placeholder:text-zinc-600" data-testid={`simulcast-dest-watch-input-${d.dest_id}`} />
                        <button type="button" onClick={saveWatch} className="rounded-md bg-[#c9772e] px-2 text-xs font-semibold text-white" data-testid={`simulcast-dest-watch-save-${d.dest_id}`}>Save</button>
                        <button type="button" onClick={() => setEditing(null)} className="px-1 text-xs text-zinc-400">Cancel</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setEditing({ dest_id: d.dest_id, value: d.watch_url || "" })} className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-[#e6a25f]" data-testid={`simulcast-dest-watch-edit-${d.dest_id}`}>
                        <ExternalLink className="h-3 w-3" /> {d.watch_url ? `Watch link: ${d.watch_url.replace(/^https?:\/\//, "")}` : "Add a “Watch on” link for the website"}
                      </button>
                    )}
                  </div>
                  {live && liveId ? (
                    <Button size="sm" variant={streaming ? "outline" : "default"} onClick={() => toggleLive(d)} disabled={busy === d.dest_id} className="h-7 text-xs" data-testid={`simulcast-dest-toggle-live-${d.dest_id}`}>
                      {busy === d.dest_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : streaming ? "Stop" : "Start"}
                    </Button>
                  ) : (
                    <Switch checked={!!d.enabled} onCheckedChange={(v) => setEnabled(d, v)} disabled={busy === d.dest_id} aria-label={`Auto-start ${d.label}`} data-testid={`simulcast-dest-enabled-${d.dest_id}`} />
                  )}
                  <button type="button" onClick={() => remove(d)} disabled={busy === d.dest_id} className="text-zinc-600 hover:text-red-400" title="Remove" data-testid={`simulcast-dest-remove-${d.dest_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <AddDestinationDialog open={adding} onOpenChange={setAdding} onSaved={load} />
    </section>
  );
}

export default SimulcastPanel;
